import type { TagSelection } from './tagSelection';
import {
  DATE_PRESETS,
  UNBOUNDED,
  activeRange,
  describeRange,
  isUnbounded,
  type DateFilterState,
  type DatePresetId,
} from './dateRange';

/**
 * The tournament list's filter, and the operator's chosen starting point for it.
 *
 * The preference is stored per room in localStorage rather than on the server: it is a view
 * preference, not data, and the API has no user-preferences surface — adding one would mean
 * an entity, a migration and endpoints for something that only affects which rows this one
 * screen opens on. The trade-off is that it does not follow an operator between machines.
 */

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 25;

export interface TournamentFilter {
  date: DateFilterState;
  /** AND semantics, applied by the server. */
  tags: string[];
  /**
   * Any one of these drops a tournament — the opposite quantifier to `tags`, so "Showdown but
   * not seniors or satellites" vetoes on either. A tag is never in both lists: the picker gives
   * each one a single three-way state, and the server refuses the contradiction anyway.
   */
  excludeTags: string[];
  pageSize: PageSize;
}

/** Where the screen opens until an operator saves something of their own. */
export const FALLBACK_FILTER: TournamentFilter = {
  date: { preset: 'thisMonth', custom: UNBOUNDED },
  tags: [],
  excludeTags: [],
  pageSize: DEFAULT_PAGE_SIZE,
};

/** No date bounds and no tags — every tournament in the room. */
export const UNFILTERED: TournamentFilter = {
  date: { preset: null, custom: UNBOUNDED },
  tags: [],
  excludeTags: [],
  pageSize: DEFAULT_PAGE_SIZE,
};

/**
 * The tag half of this filter as the shared picker sees it, and back again. The filter stores the
 * two lists under the API's own names (`tags` / `excludeTags`) because that is what goes on the
 * wire; the picker is used somewhere with no API call behind it at all, so it has its own
 * vocabulary. These two functions are the whole of the translation.
 */
export function tagSelectionOf(filter: TournamentFilter): TagSelection {
  return { include: filter.tags, exclude: filter.excludeTags };
}

export function withTagSelection(
  filter: TournamentFilter,
  selection: TagSelection,
): TournamentFilter {
  return { ...filter, tags: selection.include, excludeTags: selection.exclude };
}

const storageKey = (roomId: string) => `brush.tournaments.filter.${roomId}`;

const PRESET_IDS = new Set<string>(DATE_PRESETS.map((p) => p.id));
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function asBound(value: unknown): string | null {
  return typeof value === 'string' && ISO_DATE.test(value) ? value : null;
}

/**
 * Anything read back from storage is untrusted — it may predate a change to this shape, or
 * have been edited by hand. An unknown preset id would fall straight through `presetRange`
 * and return undefined, so every field is checked rather than cast.
 */
function parseFilter(raw: unknown): TournamentFilter | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const value = raw as Record<string, unknown>;

  const date = typeof value.date === 'object' && value.date !== null
    ? (value.date as Record<string, unknown>)
    : null;
  if (date === null) return null;

  const preset =
    typeof date.preset === 'string' && PRESET_IDS.has(date.preset)
      ? (date.preset as DatePresetId)
      : null;

  const custom = typeof date.custom === 'object' && date.custom !== null
    ? (date.custom as Record<string, unknown>)
    : {};

  const stringList = (raw: unknown): string[] =>
    Array.isArray(raw) ? raw.filter((t): t is string => typeof t === 'string') : [];

  const tags = stringList(value.tags);
  // Absent in anything saved before exclusion existed, which is most stored filters — an empty
  // list is the right reading of "this operator never excluded anything".
  const excludeTags = stringList(value.excludeTags).filter((t) => !tags.includes(t));

  const pageSize = PAGE_SIZES.find((size) => size === value.pageSize) ?? DEFAULT_PAGE_SIZE;

  return {
    date: {
      preset,
      custom: { start: asBound(custom.start), end: asBound(custom.end) },
    },
    tags,
    excludeTags,
    pageSize,
  };
}

/** The operator's saved starting point, or null if they have never saved one. */
export function loadSavedFilter(roomId: string): TournamentFilter | null {
  try {
    const raw = window.localStorage.getItem(storageKey(roomId));
    return raw === null ? null : parseFilter(JSON.parse(raw));
  } catch {
    // Private browsing, a disabled store, or malformed JSON. Falling back is correct;
    // a filter preference is never worth failing a screen over.
    return null;
  }
}

export function saveFilter(roomId: string, filter: TournamentFilter): void {
  try {
    window.localStorage.setItem(storageKey(roomId), JSON.stringify(filter));
  } catch {
    /* see loadSavedFilter */
  }
}

export function clearSavedFilter(roomId: string): void {
  try {
    window.localStorage.removeItem(storageKey(roomId));
  } catch {
    /* see loadSavedFilter */
  }
}

/** What the screen opens on: the operator's saved filter, otherwise this month. */
export function initialFilter(roomId: string | null): TournamentFilter {
  return (roomId === null ? null : loadSavedFilter(roomId)) ?? FALLBACK_FILTER;
}

export function sameFilter(a: TournamentFilter, b: TournamentFilter): boolean {
  const range = (f: TournamentFilter) => activeRange(f.date);
  const sameTags = (x: string[], y: string[]) =>
    x.length === y.length && x.every((tag) => y.includes(tag));

  return (
    a.date.preset === b.date.preset &&
    range(a).start === range(b).start &&
    range(a).end === range(b).end &&
    a.pageSize === b.pageSize &&
    sameTags(a.tags, b.tags) &&
    sameTags(a.excludeTags, b.excludeTags)
  );
}

export function isFiltered(filter: TournamentFilter): boolean {
  return (
    filter.tags.length > 0 ||
    filter.excludeTags.length > 0 ||
    !isUnbounded(activeRange(filter.date))
  );
}

/** Short labels for the collapsed bar, so a hidden filter is never a silent one. */
export function summarise(filter: TournamentFilter): string[] {
  const chips: string[] = [];
  const range = activeRange(filter.date);

  if (filter.date.preset !== null) {
    chips.push(DATE_PRESETS.find((p) => p.id === filter.date.preset)?.label ?? 'Dated');
  } else if (!isUnbounded(range)) {
    chips.push(describeRange(range));
  }

  if (filter.tags.length === 1) chips.push(filter.tags[0]!);
  else if (filter.tags.length > 1) chips.push(`${filter.tags.length} tags`);

  // Spelled out rather than counted at one, because "not Seniors" is the whole point of the
  // filter and a chip reading "1 excluded" would make the operator open the panel to find out
  // which. The header exists so a collapsed filter is never a silent one.
  if (filter.excludeTags.length === 1) chips.push(`not ${filter.excludeTags[0]!}`);
  else if (filter.excludeTags.length > 1) chips.push(`${filter.excludeTags.length} excluded`);

  return chips;
}

// The request a filter becomes, and the selection a bulk tag apply writes to, are in
// `tournamentList.ts`: a preset goes to the server by name and is resolved at the room's day, and
// the apply takes the dates the page says it applied — never dates worked out here.
